<script setup lang="ts">
  import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuSeparator,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuPortal,
    DropdownMenuSubContent
  } from '@gat-ship/vue-components/dropdown-menu'
  import {
    type DropdownMenuContentProps,
    type PopoverContentProps
  } from 'reka-ui'
  import { ref, type HTMLAttributes } from 'vue'
  import GatButton, { type GatButtonProps } from '../gat-button/gat-button.vue'
  import { GatIcon, type GatIconProps } from '../gat-icon'
  import { useGatDropdownMenuProps } from './composables/useGatDropdownMenuProps'
  import type { BaseActionDropdownItem } from '@gat-ship/types'
  import { cn } from '../../lib/utils'

  export interface GatDropdownMenuItem {
    text: string
    as?: 'separator' | 'title+separator' | 'label'
    icon?: GatIconProps['icon']
    iconProps?: GatIconProps
    checked?: boolean
    active?: boolean
    onClick?: () => void
    disabled?: boolean
    children?: GatDropdownMenuItem[]
  }

  export interface GatDropdownMenuProps extends GatButtonProps {
    options?: {
      align?: PopoverContentProps['align']
      side?: PopoverContentProps['side']
    }
    items?: BaseActionDropdownItem[]
    contentProps?: DropdownMenuContentProps & {
      class?: HTMLAttributes['class']
    }
    open?: boolean
  }

  defineOptions({ inheritAttrs: false })

  const props = withDefaults(defineProps<GatDropdownMenuProps>(), {
    open: false
  })

  const open = defineModel<boolean>('open', { required: false, default: false })

  function showChecked(item: BaseActionDropdownItem) {
    return typeof item.checked !== 'undefined'
  }

  const { gatButtonProps, onActivatorClicked } = useGatDropdownMenuProps(props)
</script>
<template>
  <template v-if="!props.items || props.items.length === 0">
    <slot name="trigger">
      <gat-button
        v-bind="gatButtonProps"
        :class="cn(props.class)"
        :data-testid="props.testId ?? props.dataTestid"
        @click="onActivatorClicked"
      />
    </slot>
  </template>
  <DropdownMenu v-else v-model:open="open">
    <DropdownMenuTrigger as-child>
      <slot name="trigger">
        <gat-button
          v-bind="gatButtonProps"
          :class="cn(props.class)"
          :data-testid="props.testId ?? props.dataTestid"
          @click="onActivatorClicked"
        />
      </slot>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      :class="cn('bg-background', contentProps?.class)"
      v-bind="contentProps"
      :side="props.options?.side ?? props.contentProps?.side ?? 'bottom'"
      :align="props.options?.align ?? props.contentProps?.align ?? 'end'"
      :data-testid="`${props.testId ?? props.dataTestid ?? 'dropdown-menu'}-content`"
    >
      <template v-for="item in props.items" :key="item.label">
        <slot name="default" :item>
          <DropdownMenuLabel
            v-if="item.title"
            class="text-foreground mt-2 py-0 text-xs font-medium"
          >
            {{ item.title }}</DropdownMenuLabel
          >
          <DropdownMenuSeparator v-if="item.separatorBefore" />
          <DropdownMenuSub v-if="item.children">
            <DropdownMenuSubTrigger>
              <gat-icon v-if="item.icon" :icon="item.icon" />
              {{ item.label }}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  v-for="child in item.children"
                  :key="child.label"
                  :class="[{ 'bg-accent': item.active }]"
                  :disabled="child.disabled"
                  :data-testid="child.testId ?? child.dataTestId"
                  @click="child.onClick"
                >
                  <DropdownMenuCheckboxItem
                    v-if="showChecked(child)"
                    :checked="child.checked"
                  />
                  <gat-icon v-else-if="child.icon" :icon="child.icon" />
                  {{ child.label }}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuItem
            v-else
            v-bind="item"
            :data-testid="item.testId ?? item.dataTestId"
            :class="[
              { 'bg-accent pointer-events-none font-medium': item.active }
            ]"
            @click="item.onClick"
          >
            <DropdownMenuCheckboxItem
              v-if="showChecked(item)"
              :checked="item.checked"
            />
            <gat-icon v-else-if="item.icon" class="h-4 w-4" :icon="item.icon" />
            {{ item.label }}
          </DropdownMenuItem>
        </slot>
      </template>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
